<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="title"
    :style="{ width: '100vw', maxWidth: '100vw', height: '100vh', margin: 0 }"
    class="image-editor-dialog"
    @hide="handleClose"
  >
    <div class="image-editor-container">
      <!-- 工具栏 -->
      <div
        class="toolbar flex items-center justify-between gap-4 flex-wrap px-2 py-2 border-b border-surface-200 dark-theme:border-surface-700"
      >
        <!-- 模式切换 -->
        <div class="flex items-center gap-2">
          <Button
            v-for="m in modes"
            :key="m.value"
            :label="$t(`workflowApps.imageEditor.modes.${m.value}`)"
            size="small"
            :severity="mode === m.value ? 'primary' : 'secondary'"
            :outlined="mode !== m.value"
            @click="mode = m.value"
          />
        </div>

        <!-- 画笔/遮罩工具选项 -->
        <div
          v-if="mode === 'mask' || mode === 'brush'"
          class="flex items-center gap-4"
        >
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">{{
              $t('workflowApps.imageEditor.brushSize')
            }}</label>
            <Slider v-model="brushSize" :min="2" :max="100" class="w-24" />
            <span class="text-sm w-8 font-mono">{{ brushSize }}</span>
          </div>
          <div v-if="mode === 'brush'" class="flex items-center gap-1">
            <button
              v-for="c in COLORS"
              :key="c"
              type="button"
              class="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
              :class="
                brushColor === c
                  ? 'border-primary scale-110'
                  : 'border-surface-400'
              "
              :style="{ backgroundColor: c }"
              @click="brushColor = c"
            />
          </div>
          <Button
            :label="$t('workflowApps.imageEditor.eraser')"
            size="small"
            :severity="isEraser ? 'warning' : 'secondary'"
            :outlined="!isEraser"
            @click="isEraser = !isEraser"
          />
        </div>

        <!-- 裁剪工具选项 -->
        <div v-if="mode === 'crop'" class="flex items-center gap-3">
          <span class="text-sm text-surface-600 dark-theme:text-surface-400">
            {{ $t('workflowApps.imageEditor.cropHint') }}
          </span>
          <div class="flex items-center gap-2">
            <input v-model="cropSnapEnabled" type="checkbox" class="rounded" />
            <label class="text-sm">{{
              $t('workflowApps.imageEditor.snap')
            }}</label>
          </div>
          <Button
            :label="$t('workflowApps.imageEditor.resetCrop')"
            size="small"
            severity="secondary"
            :disabled="imgWidth <= 0 || imgHeight <= 0"
            @click="resetCropBox"
          />
        </div>

        <!-- overlay 提示 -->
        <div v-if="mode === 'overlay'" class="flex items-center gap-2">
          <span class="text-sm text-surface-600 dark-theme:text-surface-400">
            {{ $t('workflowApps.imageEditor.overlayHint') }}
          </span>
        </div>

        <!-- 操作按钮 -->
        <div class="flex items-center gap-2">
          <Button
            :label="$t('workflowApps.imageEditor.confirm')"
            icon="pi pi-check"
            @click="handleSave"
          />
          <Button
            v-if="showNoEdit"
            :label="$t('workflowApps.imageEditor.noEdit')"
            severity="secondary"
            outlined
            @click="handleNoEdit"
          />
        </div>
      </div>

      <!-- 画布区域 -->
      <div
        ref="workAreaRef"
        class="work-area"
        :class="{ 'work-area-overlay': mode === 'overlay' }"
        @mousedown="handlePointerDown"
        @mousemove="handlePointerMove"
        @mouseup="handlePointerUp"
        @mouseleave="handlePointerUp"
      >
        <div v-if="loadError" class="load-error text-center py-12">
          <p class="font-medium text-red-400">
            {{ $t('workflowApps.imageEditor.loadError') }}
          </p>
        </div>
        <div
          v-else-if="!imageLoaded"
          class="loading-placeholder text-surface-500"
        >
          {{ $t('workflowApps.imageEditor.loading') }}
        </div>
        <template v-else>
          <div
            class="canvas-section"
            :class="{ 'flex-row': mode === 'overlay' }"
          >
            <div
              class="canvas-wrapper"
              :style="{ transform: `scale(${zoomPercent / 100})` }"
            >
              <!-- 透明背景格 -->
              <div class="checker-bg" />
              <canvas
                ref="canvasRef"
                :width="imgWidth"
                :height="imgHeight"
                class="editor-canvas"
                :class="{
                  'cursor-none': mode === 'mask' || mode === 'brush',
                  'cursor-crosshair': mode === 'crop'
                }"
              />
              <!-- 裁剪框 overlay -->
              <div
                v-if="
                  mode === 'crop' &&
                  imgWidth > 0 &&
                  imgHeight > 0 &&
                  cropBox.width > 0 &&
                  cropBox.height > 0
                "
                class="crop-overlay-container"
              >
                <!-- 吸附参考线 - 仅拖动过程中且对齐时显示红色 -->
                <template v-if="cropDragRef">
                  <div
                    v-for="v in cropSnappedGuidesComputed.vertical"
                    :key="'v-' + v"
                    class="crop-snap-guide crop-snap-guide-vertical"
                    :style="{
                      left: imgWidth > 0 ? `${(v / imgWidth) * 100}%` : 0
                    }"
                  />
                  <div
                    v-for="h in cropSnappedGuidesComputed.horizontal"
                    :key="'h-' + h"
                    class="crop-snap-guide crop-snap-guide-horizontal"
                    :style="{
                      top: imgHeight > 0 ? `${(h / imgHeight) * 100}%` : 0
                    }"
                  />
                </template>
                <div class="crop-overlay-top" :style="cropOverlayTopStyle" />
                <div
                  class="crop-overlay-bottom"
                  :style="cropOverlayBottomStyle"
                />
                <div class="crop-overlay-left" :style="cropOverlayLeftStyle" />
                <div
                  class="crop-overlay-right"
                  :style="cropOverlayRightStyle"
                />
                <div class="crop-box-border" :style="cropBoxBorderStyle" />
                <!-- 裁剪框内部 - 移动 -->
                <div
                  class="crop-box-inner"
                  :style="cropBoxBorderStyle"
                  @mousedown.stop="handlePointerDown"
                />
                <!-- 8 个拖拽手柄 -->
                <div
                  v-for="h in cropHandles"
                  :key="h.name"
                  class="crop-handle"
                  :style="getCropHandleStyle(h)"
                  @mousedown.stop="handlePointerDown"
                />
              </div>
              <!-- 叠加模式 - 选中项的边框和手柄 -->
              <template v-if="mode === 'overlay' && selectedOverlayId">
                <div
                  v-if="selectedOverlayEl"
                  class="overlay-selection-box"
                  :style="selectedOverlayBoxStyle"
                >
                  <div
                    class="overlay-selection-inner"
                    @mousedown.stop="
                      (e: MouseEvent) => handleOverlayDragStart(e)
                    "
                  />
                  <div
                    v-for="hh in overlayHandles"
                    :key="hh.name"
                    class="overlay-handle"
                    :style="getOverlayHandleStyle(hh)"
                    @mousedown.stop="
                      (e: MouseEvent) => handleOverlayResizeStart(e, hh.name)
                    "
                  />
                </div>
              </template>
            </div>
            <!-- overlay 模式 - 右侧图片列表 -->
            <div v-if="mode === 'overlay'" class="overlay-panel">
              <div class="overlay-panel-header">
                <span class="font-medium text-sm">{{
                  $t('workflowApps.imageEditor.overlayList')
                }}</span>
                <div class="flex gap-1">
                  <input
                    ref="overlayInputRef"
                    type="file"
                    accept="image/*"
                    multiple
                    class="hidden"
                    @change="handleOverlayFileSelect"
                  />
                  <Button
                    :label="$t('workflowApps.imageEditor.addOverlay')"
                    size="small"
                    icon="pi pi-upload"
                    @click="overlayInputRef?.click()"
                  />
                  <Button
                    :label="$t('workflowApps.imageEditor.selectOverlay')"
                    size="small"
                    icon="pi pi-plus"
                    severity="secondary"
                    outlined
                    @click="showOverlaySelector = true"
                  />
                </div>
              </div>
              <div class="overlay-panel-list">
                <template v-if="overlayImages.length === 0">
                  <p class="text-surface-500 text-sm py-6 text-center">
                    {{ $t('workflowApps.imageEditor.noOverlayHint') }}
                  </p>
                </template>
                <div
                  v-for="(o, idx) in overlayImages"
                  :key="o.id"
                  class="overlay-list-item"
                  :class="{
                    'overlay-list-item-selected': selectedOverlayId === o.id
                  }"
                  @click="selectedOverlayId = o.id"
                >
                  <img :src="o.url" alt="" class="overlay-thumb" />
                  <div class="overlay-item-controls">
                    <div class="flex gap-1 text-xs items-center flex-wrap">
                      <span class="text-surface-500">X</span>
                      <input
                        :value="o.x"
                        type="number"
                        class="overlay-input"
                        @click.stop
                        @input="
                          (e: Event) =>
                            updateOverlay(
                              o.id,
                              'x',
                              Number((e.target as HTMLInputElement).value)
                            )
                        "
                      />
                      <span class="text-surface-500">Y</span>
                      <input
                        :value="o.y"
                        type="number"
                        class="overlay-input"
                        @click.stop
                        @input="
                          (e: Event) =>
                            updateOverlay(
                              o.id,
                              'y',
                              Number((e.target as HTMLInputElement).value)
                            )
                        "
                      />
                      <span class="text-surface-500">{{
                        $t('workflowApps.imageEditor.scale')
                      }}</span>
                      <input
                        :value="Math.round(o.scale * 100) / 100"
                        type="number"
                        step="0.01"
                        min="0.01"
                        class="overlay-input"
                        @click.stop
                        @input="
                          (e: Event) =>
                            updateOverlayScale(
                              o.id,
                              (e.target as HTMLInputElement).value
                            )
                        "
                      />
                    </div>
                    <div class="flex gap-1 mt-1">
                      <button
                        type="button"
                        class="overlay-btn"
                        :disabled="idx <= 0"
                        :title="$t('workflowApps.imageEditor.moveUp')"
                        @click.stop="moveOverlay(o.id, -1)"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        class="overlay-btn"
                        :disabled="idx >= overlayImages.length - 1"
                        :title="$t('workflowApps.imageEditor.moveDown')"
                        @click.stop="moveOverlay(o.id, 1)"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        class="overlay-btn overlay-btn-delete"
                        :title="$t('workflowApps.imageEditor.remove')"
                        @click.stop="removeOverlay(o.id)"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- 画笔光标 - Teleport 到 body 避免被 scale 影响 -->
      <Teleport to="body">
        <div
          v-if="brushCursor && (mode === 'mask' || mode === 'brush') && visible"
          class="brush-cursor"
          :style="brushCursorStyle"
        />
      </Teleport>

      <!-- 缩放控制 -->
      <div class="zoom-controls flex items-center justify-center gap-2 py-2">
        <Button
          icon="pi pi-minus"
          size="small"
          @click="zoomPercent = Math.max(10, zoomPercent - 10)"
        />
        <span class="text-sm w-16 text-center">{{ zoomPercent }}%</span>
        <Button
          icon="pi pi-plus"
          size="small"
          @click="zoomPercent = Math.min(400, zoomPercent + 10)"
        />
      </div>
    </div>
  </Dialog>

  <!-- 选择文件弹窗（上传/生成的图片） -->
  <ImageSelectorDialog
    :visible="showOverlaySelector"
    :app-id="props.appId"
    input-type="file.image"
    @update:visible="showOverlaySelector = $event"
    @select="handleOverlaySelectFromDialog"
  />
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Slider from 'primevue/slider'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import ImageSelectorDialog from '@/components/common/ImageSelectorDialog.vue'
import { FASTAPI_CONFIG } from '@/config/fastapi'

const COLORS = [
  '#000000',
  '#FFFFFF',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF'
]

type ImageEditorMode = 'mask' | 'brush' | 'crop' | 'overlay'

interface CropBox {
  x: number
  y: number
  width: number
  height: number
}

interface OverlayImage {
  id: string
  url: string
  x: number
  y: number
  scale: number
}

const modes = [
  { value: 'mask' as const },
  { value: 'brush' as const },
  { value: 'crop' as const },
  { value: 'overlay' as const }
]

interface Props {
  modelValue: boolean
  imageUrl: string
  sourceLoadUrl?: string | null
  title?: string
  showNoEdit?: boolean
  appId?: number | null
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm', file: File): void
  (e: 'no-edit'): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  title: '编辑图片',
  showNoEdit: true
})

const emit = defineEmits<Emits>()

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v)
})

const canvasRef = ref<HTMLCanvasElement>()
const workAreaRef = ref<HTMLDivElement>()
const overlayInputRef = ref<HTMLInputElement>()
const showOverlaySelector = ref(false)

const mode = ref<ImageEditorMode>('crop')
const brushSize = ref(20)
const brushColor = ref('#FFFFFF')
const isEraser = ref(false)
const cropBox = ref<CropBox>({ x: 0, y: 0, width: 0, height: 0 })
const cropSnapEnabled = ref(true)
const overlayImages = ref<OverlayImage[]>([])
const imageLoaded = ref(false)
const loadError = ref(false)
const imgWidth = ref(0)
const imgHeight = ref(0)
const zoomPercent = ref(100)
const brushCursor = ref<{ x: number; y: number; sizePx: number } | null>(null)

const imgRef = ref<HTMLImageElement | null>(null)
const maskDataRef = ref<ImageData | null>(null)
let brushLayerCanvas: HTMLCanvasElement | null = null
let maskRedrawRAF: number | null = null
const isDrawingRef = ref(false)
const lastPosRef = ref<{ x: number; y: number } | null>(null)
const cropDragRef = ref<{
  type: 'move' | 'resize'
  handle?: string
  startX: number
  startY: number
  startBox: CropBox
} | null>(null)
const overlayImgCache = new Map<string, HTMLImageElement>()
const dragOverlayRef = ref<string | null>(null)
const dragStartOffsetRef = ref({ x: 0, y: 0 })
const selectedOverlayId = ref<string | null>(null)
const overlayResizeDragRef = ref<{
  overlayId: string
  handle: string
  startX: number
  startY: number
  startOverlay: { x: number; y: number; scale: number }
} | null>(null)
const workAreaSize = ref({ w: 0, h: 0 })
const initialZoomSet = ref(false)

const CROP_HANDLE_CURSORS: Record<string, string> = {
  nw: 'nwse-resize',
  n: 'n-resize',
  ne: 'nesw-resize',
  e: 'e-resize',
  se: 'nwse-resize',
  s: 's-resize',
  sw: 'nesw-resize',
  w: 'w-resize'
}

const cropHandles = [
  { name: 'nw' },
  { name: 'n' },
  { name: 'ne' },
  { name: 'e' },
  { name: 'se' },
  { name: 's' },
  { name: 'sw' },
  { name: 'w' }
]

const overlayHandles = [
  { name: 'nw', cx: 0, cy: 0 },
  { name: 'n', cx: 50, cy: 0 },
  { name: 'ne', cx: 100, cy: 0 },
  { name: 'e', cx: 100, cy: 50 },
  { name: 'se', cx: 100, cy: 100 },
  { name: 's', cx: 50, cy: 100 },
  { name: 'sw', cx: 0, cy: 100 },
  { name: 'w', cx: 0, cy: 50 }
]

const loadImageUrl = (): string => {
  const url = props.imageUrl
  if (url.startsWith('comfyui:')) {
    return props.sourceLoadUrl || ''
  }
  return url
}

const loadImage = async (): Promise<HTMLImageElement | null> => {
  const url = loadImageUrl()
  if (!url) {
    loadError.value = true
    return null
  }
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => {
      loadError.value = true
      resolve(null)
    }
    img.src = url
  })
}

const getCanvasPointSimple = (
  clientX: number,
  clientY: number
): { x: number; y: number } | null => {
  const canvas = canvasRef.value
  if (!canvas) return null
  const rect = canvas.getBoundingClientRect()
  return {
    x: ((clientX - rect.left) * canvas.width) / rect.width,
    y: ((clientY - rect.top) * canvas.height) / rect.height
  }
}

const cropBoxBorderStyle = computed(() => {
  const { x, y, width, height } = cropBox.value
  const w = imgWidth.value
  const h = imgHeight.value
  if (w <= 0 || h <= 0) return {}
  return {
    left: `${(x / w) * 100}%`,
    top: `${(y / h) * 100}%`,
    width: `${(width / w) * 100}%`,
    height: `${(height / h) * 100}%`
  }
})

const cropOverlayTopStyle = computed(() => {
  const { y } = cropBox.value
  const h = imgHeight.value
  if (h <= 0 || y <= 0) return { display: 'none' }
  return { top: 0, left: 0, right: 0, height: `${(y / h) * 100}%` }
})
const cropOverlayBottomStyle = computed(() => {
  const { y, height } = cropBox.value
  const h = imgHeight.value
  const bottom = y + height
  if (h <= 0 || bottom >= h) return { display: 'none' }
  return {
    bottom: 0,
    left: 0,
    right: 0,
    height: `${((h - bottom) / h) * 100}%`
  }
})
const cropOverlayLeftStyle = computed(() => {
  const { x, y, height } = cropBox.value
  const w = imgWidth.value
  const h = imgHeight.value
  const visTop = Math.max(0, Math.min(h, y))
  const visBottom = Math.max(0, Math.min(h, y + height))
  const midH = Math.max(0, (visBottom - visTop) / h) * 100
  const midTop = (visTop / h) * 100
  if (w <= 0 || x <= 0 || midH <= 0) return { display: 'none' }
  return {
    top: `${midTop}%`,
    left: 0,
    width: `${(x / w) * 100}%`,
    height: `${midH}%`
  }
})
const cropOverlayRightStyle = computed(() => {
  const { x, y, width, height } = cropBox.value
  const w = imgWidth.value
  const h = imgHeight.value
  const right = x + width
  const visTop = Math.max(0, Math.min(h, y))
  const visBottom = Math.max(0, Math.min(h, y + height))
  const midH = Math.max(0, (visBottom - visTop) / h) * 100
  const midTop = (visTop / h) * 100
  if (w <= 0 || right >= w || midH <= 0) return { display: 'none' }
  return {
    top: `${midTop}%`,
    right: 0,
    width: `${((w - right) / w) * 100}%`,
    height: `${midH}%`
  }
})

const brushCursorStyle = computed(() => {
  const c = brushCursor.value
  if (!c) return {}
  return {
    left: `${c.x}px`,
    top: `${c.y}px`,
    width: `${Math.max(4, c.sizePx)}px`,
    height: `${Math.max(4, c.sizePx)}px`,
    transform: 'translate(-50%, -50%)'
  }
})

const selectedOverlayEl = computed(() => {
  if (!selectedOverlayId.value) return null
  return (
    overlayImages.value.find((x) => x.id === selectedOverlayId.value) ?? null
  )
})

const selectedOverlayBoxStyle = computed(() => {
  const o = selectedOverlayEl.value
  if (!o) return {}
  const oImg = overlayImgCache.get(o.url)
  if (!oImg?.complete || !oImg.naturalWidth) return {}
  const w = imgWidth.value
  const h = imgHeight.value
  if (w <= 0 || h <= 0) return {}
  const ow = oImg.naturalWidth * o.scale
  const oh = oImg.naturalHeight * o.scale
  return {
    left: `${(o.x / w) * 100}%`,
    top: `${(o.y / h) * 100}%`,
    width: `${(ow / w) * 100}%`,
    height: `${(oh / h) * 100}%`,
    borderWidth: `${Math.max(2, 200 / zoomPercent.value)}px`
  }
})

const getCropHandleStyle = (h: { name: string }) => {
  const { x, y, width, height } = cropBox.value
  const w = imgWidth.value
  const hh = imgHeight.value
  if (w <= 0 || hh <= 0) return {}
  const hx =
    x +
    (h.name.includes('e')
      ? width
      : h.name === 'n' || h.name === 's'
        ? width / 2
        : 0)
  const hy =
    y +
    (h.name.includes('s')
      ? height
      : h.name === 'e' || h.name === 'w'
        ? height / 2
        : 0)
  const size = Math.max(8, 800 / zoomPercent.value)
  return {
    left: `${(hx / w) * 100}%`,
    top: `${(hy / hh) * 100}%`,
    width: `${size}px`,
    height: `${size}px`,
    transform: 'translate(-50%, -50%)',
    cursor: CROP_HANDLE_CURSORS[h.name]
  }
}

const getOverlayHandleStyle = (hh: {
  name: string
  cx: number
  cy: number
}) => {
  const size = Math.max(8, 800 / zoomPercent.value)
  return {
    left: `${hh.cx}%`,
    top: `${hh.cy}%`,
    width: `${size}px`,
    height: `${size}px`,
    transform: 'translate(-50%, -50%)',
    cursor: CROP_HANDLE_CURSORS[hh.name]
  }
}

const resetCropBox = () => {
  cropBox.value = { x: 0, y: 0, width: imgWidth.value, height: imgHeight.value }
}

const drawBase = (ctx: CanvasRenderingContext2D) => {
  const img = imgRef.value
  if (!img) return
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  ctx.drawImage(img, 0, 0)
}

const ensureBrushLayer = (): CanvasRenderingContext2D | null => {
  const w = imgWidth.value
  const h = imgHeight.value
  if (w <= 0 || h <= 0) return null
  if (
    !brushLayerCanvas ||
    brushLayerCanvas.width !== w ||
    brushLayerCanvas.height !== h
  ) {
    brushLayerCanvas = document.createElement('canvas')
    brushLayerCanvas.width = w
    brushLayerCanvas.height = h
  }
  return brushLayerCanvas.getContext('2d')
}

const redraw = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  drawBase(ctx)
  if (mode.value === 'mask') {
    if (!maskDataRef.value && canvas.width > 0 && canvas.height > 0) {
      maskDataRef.value = ctx.createImageData(canvas.width, canvas.height)
      const d = maskDataRef.value.data
      for (let i = 0; i < d.length; i += 4) {
        d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 255
      }
    }
    if (maskDataRef.value) {
      const maskCanvas = document.createElement('canvas')
      maskCanvas.width = canvas.width
      maskCanvas.height = canvas.height
      const mCtx = maskCanvas.getContext('2d')
      if (mCtx) {
        mCtx.putImageData(maskDataRef.value, 0, 0)
        const overlayCanvas = document.createElement('canvas')
        overlayCanvas.width = canvas.width
        overlayCanvas.height = canvas.height
        const oCtx = overlayCanvas.getContext('2d')
        if (oCtx) {
          oCtx.fillStyle = 'rgba(0,0,0,0.6)'
          oCtx.fillRect(0, 0, canvas.width, canvas.height)
          oCtx.globalCompositeOperation = 'destination-out'
          oCtx.drawImage(maskCanvas, 0, 0)
          oCtx.globalCompositeOperation = 'source-over'
          ctx.drawImage(overlayCanvas, 0, 0)
        }
      }
    }
  }
  if (mode.value === 'brush' && brushLayerCanvas) {
    ctx.drawImage(brushLayerCanvas, 0, 0)
  }
  if (mode.value === 'overlay') {
    overlayImages.value.forEach((o) => {
      let oImg = overlayImgCache.get(o.url)
      if (!oImg) {
        oImg = new Image()
        oImg.crossOrigin = 'anonymous'
        oImg.src = o.url
        overlayImgCache.set(o.url, oImg)
      }
      if (oImg.complete && (oImg.naturalWidth || oImg.width)) {
        const w = (oImg.naturalWidth || oImg.width) * o.scale
        const h = (oImg.naturalHeight || oImg.height) * o.scale
        ctx.drawImage(oImg, o.x, o.y, w, h)
      } else {
        oImg.onload = () => redraw()
      }
    })
  }
}

const drawAt = (x: number, y: number) => {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const size = brushSize.value
  if (mode.value === 'mask') {
    const data = maskDataRef.value
    if (!data) return
    for (let dy = -size; dy <= size; dy++) {
      for (let dx = -size; dx <= size; dx++) {
        if (dx * dx + dy * dy > size * size) continue
        const px = Math.round(x + dx)
        const py = Math.round(y + dy)
        if (px < 0 || px >= data.width || py < 0 || py >= data.height) continue
        const i = (py * data.width + px) * 4
        const alpha = isEraser.value ? 255 : 0
        data.data[i] = data.data[i + 1] = data.data[i + 2] = 255
        data.data[i + 3] = alpha
      }
    }
    if (!maskRedrawRAF) {
      maskRedrawRAF = requestAnimationFrame(() => {
        maskRedrawRAF = null
        redraw()
      })
    }
    return
  }
  if (mode.value === 'brush') {
    const brushCtx = ensureBrushLayer()
    if (!brushCtx) return
    brushCtx.beginPath()
    brushCtx.arc(x, y, size, 0, Math.PI * 2)
    if (isEraser.value) {
      brushCtx.globalCompositeOperation = 'destination-out'
      brushCtx.fillStyle = 'rgba(255,255,255,1)'
    } else {
      brushCtx.fillStyle = brushColor.value
    }
    brushCtx.fill()
    if (isEraser.value) brushCtx.globalCompositeOperation = 'source-over'
    redraw()
  }
}

const HANDLE_SIZE = 24
const CROP_SNAP_THRESHOLD = 8

/** 原图吸附参考线：左边、中线、右边；上边、中线、下边 */
const getImageGuides = (w: number, h: number) => ({
  vertical: [0, w / 2, w] as number[],
  horizontal: [0, h / 2, h] as number[]
})

/** 裁剪框边缘/中线吸附到原图参考线，返回调整后的 box 和被吸附的参考线 */
const applyCropSnap = (
  box: CropBox,
  imgW: number,
  imgH: number
): { box: CropBox; snappedV: number[]; snappedH: number[] } => {
  const guides = getImageGuides(imgW, imgH)
  const th = CROP_SNAP_THRESHOLD
  const snappedV: number[] = []
  const snappedH: number[] = []
  let { x, y, width, height } = box
  const left = x
  const right = x + width
  const centerX = x + width / 2
  const top = y
  const bottom = y + height
  const centerY = y + height / 2

  const snapTo = (val: number, targets: number[]) => {
    for (const t of targets) {
      if (Math.abs(val - t) <= th) return t
    }
    return null
  }

  const sl = snapTo(left, guides.vertical)
  if (sl !== null) {
    x = sl
    snappedV.push(sl)
  }
  const sr = snapTo(right, guides.vertical)
  if (sr !== null) {
    width = sr - x
    if (!snappedV.includes(sr)) snappedV.push(sr)
  }
  const scx = snapTo(centerX, guides.vertical)
  if (scx !== null && sl === null && sr === null) {
    x = scx - width / 2
    if (!snappedV.includes(scx)) snappedV.push(scx)
  }

  const st = snapTo(top, guides.horizontal)
  if (st !== null) {
    y = st
    snappedH.push(st)
  }
  const sb = snapTo(bottom, guides.horizontal)
  if (sb !== null) {
    height = sb - y
    if (!snappedH.includes(sb)) snappedH.push(sb)
  }
  const scy = snapTo(centerY, guides.horizontal)
  if (scy !== null && st === null && sb === null) {
    y = scy - height / 2
    if (!snappedH.includes(scy)) snappedH.push(scy)
  }

  const minDim = 20
  if (width < minDim) width = minDim
  if (height < minDim) height = minDim

  return {
    box: {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(width),
      height: Math.round(height)
    },
    snappedV,
    snappedH
  }
}

/** 检测当前裁剪框与参考线的对齐情况（用于显示红色吸附线） */
const cropSnappedGuidesComputed = computed(() => {
  if (!cropSnapEnabled.value || imgWidth.value <= 0 || imgHeight.value <= 0) {
    return { vertical: [] as number[], horizontal: [] as number[] }
  }
  const { x, y, width, height } = cropBox.value
  if (width <= 0 || height <= 0) return { vertical: [], horizontal: [] }
  const guides = getImageGuides(imgWidth.value, imgHeight.value)
  const th = 1
  const snappedV: number[] = []
  const snappedH: number[] = []
  const left = x
  const right = x + width
  const centerX = x + width / 2
  const top = y
  const bottom = y + height
  const centerY = y + height / 2
  for (const g of guides.vertical) {
    if (
      Math.abs(left - g) <= th ||
      Math.abs(right - g) <= th ||
      Math.abs(centerX - g) <= th
    ) {
      snappedV.push(g)
    }
  }
  for (const g of guides.horizontal) {
    if (
      Math.abs(top - g) <= th ||
      Math.abs(bottom - g) <= th ||
      Math.abs(centerY - g) <= th
    ) {
      snappedH.push(g)
    }
  }
  return { vertical: snappedV, horizontal: snappedH }
})

const getCropDragTarget = (px: number, py: number): string | null => {
  const { x, y, width, height } = cropBox.value
  if (width <= 0 || height <= 0) return null
  const handles: [string, number, number][] = [
    ['nw', x, y],
    ['n', x + width / 2, y],
    ['ne', x + width, y],
    ['e', x + width, y + height / 2],
    ['se', x + width, y + height],
    ['s', x + width / 2, y + height],
    ['sw', x, y + height],
    ['w', x, y + height / 2]
  ]
  for (const [name, hx, hy] of handles) {
    if (Math.abs(px - hx) <= HANDLE_SIZE && Math.abs(py - hy) <= HANDLE_SIZE)
      return name
  }
  if (px >= x && px <= x + width && py >= y && py <= y + height) return 'move'
  return null
}

const handleCropPointerDown = (clientX: number, clientY: number) => {
  const p = getCanvasPointSimple(clientX, clientY)
  if (!p) return
  const target = getCropDragTarget(p.x, p.y)
  if (!target) return
  cropDragRef.value = {
    type: target === 'move' ? 'move' : 'resize',
    handle: target === 'move' ? undefined : target,
    startX: p.x,
    startY: p.y,
    startBox: { ...cropBox.value }
  }
}

const handleCropPointerMove = (clientX: number, clientY: number) => {
  if (!cropDragRef.value) return
  const p = getCanvasPointSimple(clientX, clientY)
  if (!p) return
  const { type, handle, startX, startY, startBox } = cropDragRef.value
  const dx = p.x - startX
  const dy = p.y - startY
  let next = { ...startBox }
  if (type === 'move') {
    next.x = Math.round(startBox.x + dx)
    next.y = Math.round(startBox.y + dy)
  } else if (handle) {
    const { x, y, width, height } = startBox
    if (handle.includes('n')) {
      next.y = y + dy
      next.height = height - dy
    }
    if (handle.includes('s')) next.height = height + dy
    if (handle.includes('w')) {
      next.x = x + dx
      next.width = width - dx
    }
    if (handle.includes('e')) next.width = width + dx
    const minDim = 20
    if (next.width < minDim) {
      next.x = next.x + next.width - minDim
      next.width = minDim
    }
    if (next.height < minDim) {
      next.y = next.y + next.height - minDim
      next.height = minDim
    }
  }
  if (cropSnapEnabled.value && imgWidth.value > 0 && imgHeight.value > 0) {
    next = applyCropSnap(next, imgWidth.value, imgHeight.value).box
  }
  cropBox.value = next
}

const handlePointerDown = (e: MouseEvent) => {
  const clientX = e.clientX
  const clientY = e.clientY
  if (mode.value === 'crop') {
    handleCropPointerDown(clientX, clientY)
    return
  }
  const p = getCanvasPointSimple(clientX, clientY)
  if (!p) return
  if (mode.value === 'overlay') {
    for (let i = overlayImages.value.length - 1; i >= 0; i--) {
      const o = overlayImages.value[i]
      const oImg = overlayImgCache.get(o.url)
      if (!oImg?.complete) continue
      const ow = (oImg.naturalWidth || oImg.width) * o.scale
      const oh = (oImg.naturalHeight || oImg.height) * o.scale
      if (p.x >= o.x && p.x <= o.x + ow && p.y >= o.y && p.y <= o.y + oh) {
        selectedOverlayId.value = o.id
        dragOverlayRef.value = o.id
        dragStartOffsetRef.value = { x: p.x - o.x, y: p.y - o.y }
        return
      }
    }
    selectedOverlayId.value = null
    return
  }
  isDrawingRef.value = true
  lastPosRef.value = p
  drawAt(p.x, p.y)
}

const handlePointerMove = (e: MouseEvent) => {
  const clientX = e.clientX
  const clientY = e.clientY
  if (mode.value === 'mask' || mode.value === 'brush') {
    const canvas = canvasRef.value
    if (canvas) {
      const rect = canvas.getBoundingClientRect()
      const inCanvas =
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      if (inCanvas) {
        const p = getCanvasPointSimple(clientX, clientY)
        if (p) {
          const scaleX = canvas.width / rect.width
          const scaleY = canvas.height / rect.height
          const sizePx = (2 * brushSize.value) / Math.min(scaleX, scaleY)
          brushCursor.value = { x: clientX, y: clientY, sizePx }
        }
      } else {
        brushCursor.value = null
      }
    }
  } else {
    brushCursor.value = null
  }
  if (mode.value === 'crop' && cropDragRef.value) {
    handleCropPointerMove(clientX, clientY)
    return
  }
  if (mode.value === 'overlay' && overlayResizeDragRef.value) {
    handleOverlayResizeMove(clientX, clientY)
    return
  }
  if (mode.value === 'overlay' && dragOverlayRef.value) {
    const p = getCanvasPointSimple(clientX, clientY)
    if (!p) return
    const o = overlayImages.value.find((x) => x.id === dragOverlayRef.value)
    if (o) {
      overlayImages.value = overlayImages.value.map((x) =>
        x.id === o.id
          ? {
              ...x,
              x: Math.round(p.x - dragStartOffsetRef.value.x),
              y: Math.round(p.y - dragStartOffsetRef.value.y)
            }
          : x
      )
      redraw()
    }
    return
  }
  if (!isDrawingRef.value || !lastPosRef.value) return
  const p = getCanvasPointSimple(clientX, clientY)
  if (!p) return
  const last = lastPosRef.value
  const steps = Math.max(Math.abs(p.x - last.x), Math.abs(p.y - last.y), 1)
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    drawAt(
      Math.round(last.x + (p.x - last.x) * t),
      Math.round(last.y + (p.y - last.y) * t)
    )
  }
  lastPosRef.value = p
}

const handlePointerUp = () => {
  if (maskRedrawRAF) {
    cancelAnimationFrame(maskRedrawRAF)
    maskRedrawRAF = null
    redraw()
  }
  cropDragRef.value = null
  isDrawingRef.value = false
  lastPosRef.value = null
  dragOverlayRef.value = null
  overlayResizeDragRef.value = null
}

const onGlobalMove = (e: MouseEvent) => {
  if (cropDragRef.value || overlayResizeDragRef.value || dragOverlayRef.value)
    handlePointerMove(e)
}
onMounted(() => {
  window.addEventListener('mousemove', onGlobalMove)
  window.addEventListener('mouseup', handlePointerUp)
  const workEl = workAreaRef.value
  if (workEl) {
    const measure = () => {
      const w = workEl.clientWidth
      const h = workEl.clientHeight
      if (w > 0 && h > 0) workAreaSize.value = { w, h }
    }
    const ro = new ResizeObserver(measure)
    ro.observe(workEl)
    measure()
  }
})

const handleOverlayFileSelect = (e: Event) => {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (!files?.length) return
  Array.from(files).forEach((file) => {
    const url = URL.createObjectURL(file)
    const id = crypto.randomUUID()
    const oImg = new Image()
    oImg.crossOrigin = 'anonymous'
    oImg.src = url
    oImg.onload = () => redraw()
    overlayImgCache.set(url, oImg)
    overlayImages.value = [
      ...overlayImages.value,
      { id, url, x: 0, y: 0, scale: 0.5 }
    ]
    selectedOverlayId.value = id
  })
  input.value = ''
  redraw()
}

/** 将可能跨域的外部图片 URL 转为后端代理 URL，避免 CORS */
const toProxyImageUrl = (url: string): string => {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/view?url=${encodeURIComponent(url)}`
  }
  return url
}

const handleOverlaySelectFromDialog = (
  _filename: string,
  imageObj?: { url?: string }
) => {
  const rawUrl = imageObj?.url
  if (!rawUrl) return
  const url = toProxyImageUrl(rawUrl)
  const id = crypto.randomUUID()
  const oImg = new Image()
  oImg.crossOrigin = 'anonymous'
  oImg.src = url
  oImg.onload = () => redraw()
  overlayImgCache.set(url, oImg)
  overlayImages.value = [
    ...overlayImages.value,
    { id, url, x: 0, y: 0, scale: 0.5 }
  ]
  selectedOverlayId.value = id
  redraw()
}

const updateOverlay = (id: string, field: 'x' | 'y', value: number) => {
  overlayImages.value = overlayImages.value.map((x) =>
    x.id === id ? { ...x, [field]: value } : x
  )
  redraw()
}

const updateOverlayScale = (id: string, value: string) => {
  const scale = Math.max(0.01, Math.round((Number(value) || 0.01) * 100) / 100)
  overlayImages.value = overlayImages.value.map((x) =>
    x.id === id ? { ...x, scale } : x
  )
  redraw()
}

const moveOverlay = (id: string, delta: number) => {
  const idx = overlayImages.value.findIndex((x) => x.id === id)
  if (idx < 0 || idx + delta < 0 || idx + delta >= overlayImages.value.length)
    return
  const arr = [...overlayImages.value]
  const [item] = arr.splice(idx, 1)
  arr.splice(idx + delta, 0, item)
  overlayImages.value = arr
  redraw()
}

const removeOverlay = (id: string) => {
  const o = overlayImages.value.find((x) => x.id === id)
  if (o) URL.revokeObjectURL(o.url)
  overlayImages.value = overlayImages.value.filter((x) => x.id !== id)
  if (selectedOverlayId.value === id) selectedOverlayId.value = null
  redraw()
}

const handleOverlayDragStart = (e: MouseEvent) => {
  const o = selectedOverlayEl.value
  if (!o) return
  const p = getCanvasPointSimple(e.clientX, e.clientY)
  if (!p) return
  dragOverlayRef.value = o.id
  dragStartOffsetRef.value = { x: p.x - o.x, y: p.y - o.y }
}

const handleOverlayResizeStart = (_e: MouseEvent, handle: string) => {
  const o = selectedOverlayEl.value
  if (!o) return
  overlayResizeDragRef.value = {
    overlayId: o.id,
    handle,
    startX: 0,
    startY: 0,
    startOverlay: { x: o.x, y: o.y, scale: o.scale }
  }
}

const handleOverlayResizeMove = (clientX: number, clientY: number) => {
  const drag = overlayResizeDragRef.value
  if (!drag) return
  const p = getCanvasPointSimple(clientX, clientY)
  if (!p) return
  const o = overlayImages.value.find((x) => x.id === drag.overlayId)
  if (!o) return
  const oImg = overlayImgCache.get(o.url)
  if (!oImg?.complete || !oImg.naturalWidth) return
  const imgW = oImg.naturalWidth
  const imgH = oImg.naturalHeight
  const { startOverlay } = drag
  const oldW = imgW * startOverlay.scale
  const oldH = imgH * startOverlay.scale
  const oldRight = startOverlay.x + oldW
  const oldBottom = startOverlay.y + oldH
  const minScale = 0.01
  const clampScale = (s: number) => Math.max(minScale, s)
  let nextX = startOverlay.x
  let nextY = startOverlay.y
  let nextScale = startOverlay.scale
  const { handle } = drag
  if (handle === 'se') {
    const nw = Math.max(1, p.x - startOverlay.x)
    const nh = Math.max(1, p.y - startOverlay.y)
    nextScale = clampScale(Math.min(nw / imgW, nh / imgH))
  } else if (handle === 'sw') {
    const nw = Math.max(1, oldRight - p.x)
    const nh = Math.max(1, p.y - startOverlay.y)
    nextScale = clampScale(Math.min(nw / imgW, nh / imgH))
    nextX = oldRight - imgW * nextScale
  } else if (handle === 'nw') {
    nextX = p.x
    nextY = p.y
    const nw = Math.max(1, oldRight - p.x)
    const nh = Math.max(1, oldBottom - p.y)
    nextScale = clampScale(Math.min(nw / imgW, nh / imgH))
    nextX = oldRight - imgW * nextScale
    nextY = oldBottom - imgH * nextScale
  } else if (handle === 'ne') {
    nextY = p.y
    const nw = Math.max(1, p.x - startOverlay.x)
    const nh = Math.max(1, oldBottom - p.y)
    nextScale = clampScale(Math.min(nw / imgW, nh / imgH))
    nextY = oldBottom - imgH * nextScale
  } else if (handle === 'e') {
    nextScale = clampScale(Math.max(1, p.x - startOverlay.x) / imgW)
  } else if (handle === 'w') {
    nextScale = clampScale(Math.max(1, oldRight - p.x) / imgW)
    nextX = oldRight - imgW * nextScale
  } else if (handle === 's') {
    nextScale = clampScale(Math.max(1, p.y - startOverlay.y) / imgH)
  } else if (handle === 'n') {
    nextScale = clampScale(Math.max(1, oldBottom - p.y) / imgH)
    nextY = oldBottom - imgH * nextScale
  }
  overlayImages.value = overlayImages.value.map((x) =>
    x.id === drag.overlayId
      ? {
          ...x,
          x: Math.round(nextX),
          y: Math.round(nextY),
          scale: Math.round(nextScale * 100) / 100
        }
      : x
  )
  redraw()
}

const saveToFile = (): Promise<File | null> => {
  const canvas = document.createElement('canvas')
  const img = imgRef.value
  if (!img) return Promise.resolve(null)

  if (mode.value === 'crop') {
    const { x, y, width: cw, height: ch } = cropBox.value
    const newW = Math.max(1, Math.round(cw))
    const newH = Math.max(1, Math.round(ch))
    canvas.width = newW
    canvas.height = newH
    const ctx = canvas.getContext('2d')
    if (!ctx) return Promise.resolve(null)
    ctx.clearRect(0, 0, newW, newH)
    const sx = Math.max(0, x)
    const sy = Math.max(0, y)
    const right = Math.min(imgWidth.value, x + cw)
    const bottom = Math.min(imgHeight.value, y + ch)
    const sw = right - sx
    const sh = bottom - sy
    const dx = sx - x
    const dy = sy - y
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, sw, sh)
  } else if (mode.value === 'overlay') {
    canvas.width = imgWidth.value
    canvas.height = imgHeight.value
    const ctx = canvas.getContext('2d')
    if (!ctx) return Promise.resolve(null)
    ctx.drawImage(img, 0, 0)
    const promises = overlayImages.value.map(
      (o) =>
        new Promise<void>((resolve) => {
          const oImg = new Image()
          oImg.crossOrigin = 'anonymous'
          oImg.onload = () => {
            ctx!.drawImage(
              oImg,
              o.x,
              o.y,
              oImg.width * o.scale,
              oImg.height * o.scale
            )
            resolve()
          }
          oImg.onerror = () => resolve()
          oImg.src = o.url
        })
    )
    return Promise.all(promises).then(
      () =>
        new Promise((resolve) => {
          canvas.toBlob(
            (blob) =>
              resolve(
                blob
                  ? new File([blob], 'edited.png', { type: 'image/png' })
                  : null
              ),
            'image/png',
            1
          )
        })
    )
  } else {
    canvas.width = imgWidth.value
    canvas.height = imgHeight.value
    const ctx = canvas.getContext('2d')
    if (!ctx) return Promise.resolve(null)
    if (mode.value === 'brush') {
      ctx.drawImage(canvasRef.value!, 0, 0)
    } else {
      ctx.drawImage(img, 0, 0)
      if (mode.value === 'mask' && maskDataRef.value) {
        const maskCanvas = document.createElement('canvas')
        maskCanvas.width = canvas.width
        maskCanvas.height = canvas.height
        const mCtx = maskCanvas.getContext('2d')
        if (mCtx) {
          mCtx.putImageData(maskDataRef.value, 0, 0)
          ctx.globalCompositeOperation = 'destination-in'
          ctx.drawImage(maskCanvas, 0, 0)
          ctx.globalCompositeOperation = 'source-over'
        }
      }
    }
  }
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) =>
        resolve(
          blob ? new File([blob], 'edited.png', { type: 'image/png' }) : null
        ),
      'image/png',
      1
    )
  })
}

const handleSave = async () => {
  const file = await saveToFile()
  if (file) {
    emit('confirm', file)
    visible.value = false
  }
}

const handleNoEdit = () => {
  emit('no-edit')
  visible.value = false
}

const handleClose = () => {
  emit('cancel')
  visible.value = false
}

watch(
  () => mode.value,
  () => {
    if (mode.value !== 'overlay') selectedOverlayId.value = null
  }
)

watch(
  () => props.modelValue,
  async (val) => {
    if (!val) {
      imageLoaded.value = false
      loadError.value = false
      initialZoomSet.value = false
      brushLayerCanvas = null
      overlayImages.value.forEach((o) => URL.revokeObjectURL(o.url))
      overlayImages.value = []
    }
  }
)

watch(
  () => [props.modelValue, props.imageUrl] as const,
  async ([visibleVal, url]) => {
    if (!visibleVal || !url) return
    loadError.value = false
    const img = await loadImage()
    if (!img) return
    imgRef.value = img
    imgWidth.value = img.width
    imgHeight.value = img.height
    cropBox.value = { x: 0, y: 0, width: img.width, height: img.height }
    imageLoaded.value = true
    maskDataRef.value = null
    brushLayerCanvas = null
    overlayImages.value = []
    await nextTick()
    const canvas = canvasRef.value
    if (canvas && mode.value === 'mask') {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        maskDataRef.value = ctx.createImageData(canvas.width, canvas.height)
        const d = maskDataRef.value.data
        for (let i = 0; i < d.length; i += 4) {
          d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 255
        }
      }
    }
    redraw()
    await nextTick()
    if (workAreaRef.value) {
      workAreaSize.value = {
        w: workAreaRef.value.clientWidth,
        h: workAreaRef.value.clientHeight
      }
    }
    setTimeout(() => {
      if (workAreaRef.value) {
        workAreaSize.value = {
          w: workAreaRef.value.clientWidth,
          h: workAreaRef.value.clientHeight
        }
      }
    }, 100)
  }
)

watch(
  () =>
    [
      imageLoaded.value,
      imgWidth.value,
      imgHeight.value,
      workAreaSize.value.w,
      workAreaSize.value.h
    ] as const,
  ([loaded, iw, ih, areaW, areaH]) => {
    if (
      !loaded ||
      iw <= 0 ||
      ih <= 0 ||
      areaW <= 0 ||
      areaH <= 0 ||
      initialZoomSet.value
    )
      return
    const maxH =
      typeof window !== 'undefined'
        ? Math.min(areaH, window.innerHeight - 140)
        : areaH
    const scaleFit = Math.min(areaW / iw, maxH / ih)
    const displayedW = iw * scaleFit
    const displayedH = ih * scaleFit
    const targetW = areaW / 2
    const targetH = areaH / 2
    const zoomScale = Math.min(targetW / displayedW, targetH / displayedH)
    zoomPercent.value = Math.round(Math.min(400, Math.max(10, zoomScale * 100)))
    initialZoomSet.value = true
  }
)

watch(
  () => [imageLoaded.value, imgWidth.value, imgHeight.value, mode.value],
  () => {
    if (imageLoaded.value && canvasRef.value && mode.value === 'crop') {
      if (cropBox.value.width <= 0 || cropBox.value.height <= 0) {
        cropBox.value = {
          x: 0,
          y: 0,
          width: imgWidth.value,
          height: imgHeight.value
        }
      }
    }
    redraw()
  }
)

onBeforeUnmount(() => {
  if (maskRedrawRAF) cancelAnimationFrame(maskRedrawRAF)
  overlayImages.value.forEach((o) => URL.revokeObjectURL(o.url))
  window.removeEventListener('mousemove', onGlobalMove)
  window.removeEventListener('mouseup', handlePointerUp)
})
</script>

<style scoped>
.image-editor-dialog :deep(.p-dialog) {
  margin: 0;
  max-height: 100vh;
}

.image-editor-dialog :deep(.p-dialog-content) {
  padding: 0;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

.image-editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.toolbar {
  flex-shrink: 0;
}

.work-area {
  flex: 1;
  overflow: auto;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.work-area-overlay {
  align-items: stretch;
}

.canvas-section {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.canvas-section.flex-row {
  flex-direction: row;
  align-items: center;
}

.canvas-wrapper {
  position: relative;
  display: inline-block;
}

.checker-bg {
  position: absolute;
  inset: 0;
  background-size: 16px 16px;
  background-position:
    0 0,
    0 8px,
    8px -8px,
    -8px 0px;
  background-image: linear-gradient(45deg, #333 25%, transparent 25%),
    linear-gradient(-45deg, #333 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #333 75%),
    linear-gradient(-45deg, transparent 75%, #333 75%);
  background-color: #222;
}

.editor-canvas {
  display: block;
  max-width: min(98vw, 1800px);
  max-height: calc(100vh - 200px);
  object-fit: contain;
  position: relative;
}

.crop-overlay-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.crop-overlay-container > .crop-box-inner,
.crop-overlay-container > .crop-handle {
  pointer-events: auto;
}

.crop-snap-guide {
  position: absolute;
  background: #ef4444;
  pointer-events: none;
  z-index: 2;
}
.crop-snap-guide-vertical {
  width: 2px;
  top: 0;
  bottom: 0;
  transform: translateX(-50%);
}
.crop-snap-guide-horizontal {
  height: 2px;
  left: 0;
  right: 0;
  transform: translateY(-50%);
}

.crop-overlay-top,
.crop-overlay-bottom,
.crop-overlay-left,
.crop-overlay-right {
  position: absolute;
  background: rgba(0, 0, 0, 0.5);
}

.crop-overlay-top {
  left: 0;
  right: 0;
}
.crop-overlay-bottom {
  left: 0;
  right: 0;
}
.crop-overlay-left {
  left: 0;
}
.crop-overlay-right {
  right: 0;
}

.crop-box-border {
  position: absolute;
  border: 2px dashed rgba(255, 255, 255, 0.9);
  box-sizing: border-box;
}

.crop-box-inner {
  position: absolute;
  cursor: move;
  box-sizing: border-box;
}

.crop-handle {
  position: absolute;
  border-radius: 50%;
  background: white;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-sizing: border-box;
}

.overlay-selection-box {
  position: absolute;
  border: 2px dashed #34d399;
  box-sizing: border-box;
  pointer-events: none;
}

.overlay-selection-box > .overlay-selection-inner,
.overlay-selection-box > .overlay-handle {
  pointer-events: auto;
}

.overlay-selection-inner {
  position: absolute;
  inset: 0;
  cursor: move;
}

.overlay-handle {
  position: absolute;
  border-radius: 50%;
  background: #34d399;
  border: 2px solid white;
  box-sizing: border-box;
}

.overlay-panel {
  width: 18rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(28, 28, 30, 0.9);
  margin: 1rem;
  border-radius: 0.75rem;
  overflow: hidden;
  align-self: center;
  max-height: calc(100vh - 180px);
}

.overlay-panel-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.overlay-panel-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
}

.overlay-list-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s;
}

.overlay-list-item:hover {
  border-color: rgba(255, 255, 255, 0.2);
}

.overlay-list-item-selected {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(52, 211, 153, 0.6);
}

.overlay-thumb {
  width: 3rem;
  height: 3rem;
  object-fit: cover;
  border-radius: 0.25rem;
}

.overlay-item-controls {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.overlay-input {
  width: 3.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: #2c2c2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.75rem;
}

.overlay-btn {
  padding: 0.25rem;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 0.25rem;
}

.overlay-btn:hover:not(:disabled) {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.overlay-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.overlay-btn-delete:hover {
  color: #f87171;
}

.brush-cursor {
  position: fixed;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  pointer-events: none;
  z-index: 100000;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
  mix-blend-mode: difference;
}

.load-error,
.loading-placeholder {
  padding: 2rem;
}
</style>
